import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { LiliButton } from '@lili/lili-frontend-common';
import { GENERAL_STYLE, FORGOT_PASSWORD_FLOW } from '../constants';
import LoginCard from '../../../components/LoginCard';
import Heading from '../../../../../generic-components/Heading';
import { LoginHelpNavBar } from '../LoginHelpNavBar';
import DigitsContainer from '../../../../../generic-components/DigitsContainer';
import { usePasswordRecoveryValidate } from '../ForgotEmail/hooks/forgotPasswordHooks';
import { ForgotPasswordGeneralErrorModal } from '../Errors/ForgotPasswordGeneralErrorModal';
import { SERVER_ERRORS_DATA } from '../../../../../constants/serverErrors';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/svg/stamp-icons/ic-stamp-timewatch.svg';
import { Layout } from '../../../../../components/Layout/Layout';

interface IProps {
  email: string;
  onSSNVerified: (customerId: string) => void;
  onClose: () => void;
  onBack: () => void;
}

const ForgotPasswordSSNInputStep = ({ email, onSSNVerified, onClose, onBack }: IProps) => {
  const { t } = useTranslation();
  const [ssnInput, setSSNInput] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);
  const [showLockedError, setShowLockedError] = useState<boolean>(false);
  const [isSSNCodeError, setIsSSNCodeError] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const numberOfDigits = 4;

  const { mutateAsync: mutateUsePasswordRecoveryValidate, isLoading: isAnsweringQuestion } =
    usePasswordRecoveryValidate((code) => {
      switch (code) {
        case SERVER_ERRORS_DATA.USER_LOCKED_CODE:
          setShowLockedError(true);
          setShowError(false);
          break;
        default:
          setShowError(true);
          setShowLockedError(false);
      }
    });
  const validate = () => {
    const body = {
      loginName: email,
      ssn: ssnInput,
    };
    mutateUsePasswordRecoveryValidate(body).then((response) => {
      onSSNVerified(response.customerId);
    });
  };
  const heading = (
    <Heading
      title={t(FORGOT_PASSWORD_FLOW.SSN_INPUT.TITLE)}
      className={`${GENERAL_STYLE.HEADING.CLASS_NAME} mb-[16px]`}
    />
  );
  const subtitle = (
    <Heading
      title={t(FORGOT_PASSWORD_FLOW.SSN_INPUT.SUBTITLE)}
      className={`${GENERAL_STYLE.DESCRIPTION.CLASS_NAME} mb-[40px]`}
    />
  );
  const mainButton = (
    <LiliButton
      id='mainButton'
      title={t(FORGOT_PASSWORD_FLOW.SSN_INPUT.CONTINUE_BUTTON_TEXT)}
      className={`${GENERAL_STYLE.MAIN_BUTTON.CLASS_NAME}`}
      onClick={validate}
      isLoading={isAnsweringQuestion}
      disabled={isButtonDisabled}
    />
  );
  const errorModal = showError && (
    <ForgotPasswordGeneralErrorModal
      title={t(FORGOT_PASSWORD_FLOW.ERRORS.INPUT_STEP_ERROR_TITLE)}
      description={t(FORGOT_PASSWORD_FLOW.ERRORS.INPUT_STEP_ERROR_DESCRIPTION)}
      onAction={() => setShowError(false)}
      onClose={() => {
        setShowError(false);
      }}
    />
  );
  const lockedOutError = showLockedError && (
    <ForgotPasswordGeneralErrorModal
      title={t(FORGOT_PASSWORD_FLOW.ERRORS.USER_LOCKED_ERROR_TITLE)}
      description={t(FORGOT_PASSWORD_FLOW.ERRORS.USER_LOCKED_ERROR_DESCRIPTION)}
      mainButtonTitle={t(FORGOT_PASSWORD_FLOW.ERRORS.ERROR_BUTTON_GOT_IT)}
      hideSecondaryButton
      icon={<ClockIcon />}
      onAction={() => setShowLockedError(false)}
      onClose={() => {
        setShowLockedError(false);
      }}
    />
  );
  return (
    <Layout shouldHideProfile shouldHideChatBot wrapperClassName='pt-[78px] pl-[72px] ml-[100px] pc-md:ml-[256px]'>
      <LoginHelpNavBar
        onBackClick={onBack}
        onCloseClick={onClose}
      />

      <LoginCard className={GENERAL_STYLE.LOGIN_CARD.CLASS_NAME}>
        <div className='flex flex-col justify-between h-full'>
          <div className='mb-[62px]'>
            {heading}
            {subtitle}
            {errorModal}
            {lockedOutError}
            <DigitsContainer
              containerClassName='flex space-x-[14px] justify-center'
              numOfDigits={numberOfDigits}
              onFulfill={(ssn) => {
                setSSNInput(ssn);
                setIsButtonDisabled(ssn.length !== numberOfDigits);
              }}
              onDigitsChanged={(ssn) => {
                setIsSSNCodeError(false);
                setSSNInput(ssn);
                setIsButtonDisabled(ssn.length !== numberOfDigits);
              }}
              isLoading={isAnsweringQuestion}
              isError={isSSNCodeError}
            />
          </div>

          {mainButton}
        </div>
      </LoginCard>
    </Layout>

  );
};

export default ForgotPasswordSSNInputStep;
